import React from "react";
import { Tooltip } from "@vibe/core";
import type { DisplayValue } from "./discrepancyGrid.interface";

/**
 * Renders a column value the way Monday renders it, chosen by the column's own type:
 * a status fills its cell in the label's colour, a dropdown becomes tags, a person gets an
 * avatar, a date gets a calendar mark. Everything else falls through to plain text.
 *
 * Only `columnType` and the value itself are needed — both already reach the grid, the type
 * via `row.meta.columnType` (threaded by subitemScanAdapter) and the colour via the scan's
 * `StatusValue { label_style { color } }` fragment. No extra Monday call is involved.
 *
 * Every colour here is a Vibe custom property rather than a literal, EXCEPT the status
 * ground, which is customer data: the label's colour is whatever the customer chose on their
 * board, so it arrives as a hex on the value and is applied as-is. That is the one colour on
 * this screen that must NOT follow our theme, because it is reporting theirs.
 */

/** Monday paints status label text white on every theme; this token resolves to white in both. */
const STATUS_TEXT_COLOR = "var(--text-color-on-brand)";

/**
 * Ground for a status whose value carries no colour — an empty status, or a producer that
 * doesn't supply one (Shared Items today). Deliberately a token rather than the flat grey
 * SyncWizardStep1 hardcodes, so it follows a theme switch instead of stranding this cell in
 * light mode.
 */
export const STATUS_FALLBACK_GROUND = "var(--ui-border-color)";

/** Avatar grounds, cycled by name so one person keeps one colour across boards and rows. */
const AVATAR_COLORS = [
  "var(--primary-color)",
  "var(--positive-color)",
  "var(--negative-color)",
  "var(--warning-color)",
];

const avatarColorFor = (name: string): string => {
  let total = 0;
  for (let index = 0; index < name.length; index += 1) total += name.charCodeAt(index);
  return AVATAR_COLORS[total % AVATAR_COLORS.length];
};

const initialsFor = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase();

/**
 * `YYYY-MM-DD` → `MMM D`, by splitting the string rather than constructing a Date from it.
 * Date-parsing a bare ISO date treats it as UTC midnight and then reports it in the viewer's
 * zone, which renders the PREVIOUS day for everyone west of UTC. Monday's date column has no
 * time component, so there is no zone to convert between and nothing here needs one.
 *
 * A test guards this at the source level — see MondayValueCell.test.tsx for why a behavioural
 * test cannot.
 */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const formatDate = (text: string): string => {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text.trim());
  if (!parts) return text;
  const month = MONTHS[Number(parts[2]) - 1];
  return month ? `${month} ${Number(parts[3])}` : text;
};

const CalendarIcon: React.FC = () => (
  <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M6.5 2v1.5h7V2H15v1.5h1.5A1.5 1.5 0 0 1 18 5v11.5A1.5 1.5 0 0 1 16.5 18h-13A1.5 1.5 0 0 1 2 16.5V5a1.5 1.5 0 0 1 1.5-1.5H5V2h1.5ZM16.5 8h-13v8.5h13V8Z" />
  </svg>
);

const ellipsis: React.CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

interface MondayValueCellProps {
  value: DisplayValue | null;
  columnType: string | null;
  /**
   * Dropdown labels, when the producer supplied them. Read from the value rather than parsed
   * out of `value.value`, whose option ids are board-local and meaningless on another board.
   */
  dropdownLabels?: string[];
}

const MondayValueCell: React.FC<MondayValueCellProps> = ({
  value,
  columnType,
  dropdownLabels,
}) => {
  // null means the scan found no counterpart for this column on that board — distinct from a
  // present-but-empty value, and from a column that does not exist there at all.
  if (value === null) {
    return (
      <span style={{ color: "var(--secondary-text-color)", fontSize: 13 }}>&mdash;</span>
    );
  }

  if (!value.exists) {
    return (
      <Tooltip content="Column does not exist on this board">
        <span style={{ color: "var(--secondary-text-color)", fontSize: 13, ...ellipsis }}>
          Does not exist
        </span>
      </Tooltip>
    );
  }

  const text = value.text?.trim() ?? "";

  if (columnType === "status") {
    // An empty status still renders its bar, as Monday does — the bar IS the value, so a
    // blank cell here would read as "no status column" rather than "no status set".
    return (
      <Tooltip content={text || "Empty"}>
        <span
          data-testid="monday-status-bar"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 26,
            padding: "0 var(--spacing-small)",
            borderRadius: "var(--border-radius-small, 4px)",
            background: value.labelColor || STATUS_FALLBACK_GROUND,
            color: STATUS_TEXT_COLOR,
            fontSize: 12,
            fontWeight: 500,
            ...ellipsis,
          }}
        >
          {text}
        </span>
      </Tooltip>
    );
  }

  if (columnType === "dropdown") {
    // Prefer the typed labels: Monday's `text` comma-joins them, which cannot tell one label
    // containing a comma from two labels — the same reason the engine carries them separately.
    const labels = dropdownLabels?.length ? dropdownLabels : text ? [text] : [];
    if (labels.length === 0) {
      return <span style={{ color: "var(--secondary-text-color)", fontSize: 13 }}>Empty</span>;
    }
    return (
      <Tooltip content={labels.join(", ")}>
        <span style={{ display: "flex", gap: "var(--spacing-xs)", overflow: "hidden" }}>
          {labels.map((label) => (
            <span
              key={label}
              style={{
                display: "inline-flex",
                alignItems: "center",
                height: 22,
                padding: "0 var(--spacing-small)",
                borderRadius: 11,
                fontSize: 12,
                whiteSpace: "nowrap",
                background: "var(--allgrey-background-color)",
                border: "1px solid var(--layout-border-color)",
                color: "var(--primary-text-color)",
              }}
            >
              {label}
            </span>
          ))}
        </span>
      </Tooltip>
    );
  }

  if (!text) {
    return <span style={{ color: "var(--secondary-text-color)", fontSize: 13 }}>Empty</span>;
  }

  if (columnType === "people") {
    // Initials, not a photo. `column_values` reports a PeopleValue's person IDS, not user
    // objects, so photos would need a second `users(ids:)` round trip on the scan's hot path
    // — deferred until the scan's latency is actually measured.
    return (
      <Tooltip content={text}>
        <span style={{ display: "flex", alignItems: "center", gap: 6, overflow: "hidden" }}>
          <span
            style={{
              width: 22,
              height: 22,
              flex: "none",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              fontWeight: 700,
              background: avatarColorFor(text),
              color: STATUS_TEXT_COLOR,
            }}
          >
            {initialsFor(text)}
          </span>
          <span style={{ fontSize: 13, ...ellipsis }}>{text}</span>
        </span>
      </Tooltip>
    );
  }

  if (columnType === "date") {
    return (
      <Tooltip content={text}>
        <span
          style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, ...ellipsis }}
        >
          <span style={{ color: "var(--secondary-text-color)", display: "inline-flex" }}>
            <CalendarIcon />
          </span>
          {formatDate(text)}
        </span>
      </Tooltip>
    );
  }

  if (columnType === "numbers") {
    return (
      <Tooltip content={text}>
        <span style={{ fontSize: 13, fontVariantNumeric: "tabular-nums", ...ellipsis }}>
          {text}
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip content={text}>
      <span style={{ fontSize: 13, ...ellipsis }}>{text}</span>
    </Tooltip>
  );
};

export default MondayValueCell;
